import { AppComponent } from './app/app.component';
import { ENVIRONMENT } from './environments/environment';
import { AppRoutingModule } from '@my-backoffice/app/app-routing.module';
import { LifeSciencesStudyMockInterceptor } from '@my-backoffice/app/pages/life-sciences/shared/interceptors/life-sciences-study-mock.interceptor';
import { Office365ErrorHandlerInterceptor } from '@my-backoffice/app/pages/login/federated/office365/interceptors/office365-error-handler.interceptor';
import { Office365HttpAuthInterceptor } from '@my-backoffice/app/pages/login/federated/office365/interceptors/office365-http-auth.interceptor';
import { Office365PermissionsMockInterceptor } from '@my-backoffice/app/pages/login/federated/office365/interceptors/office365-permissions-mock.interceptor';
import { Office365AuthenticationService } from '@my-backoffice/app/pages/login/federated/office365/services/office365-authentication.service';
import { AUTHENTICATION_SERVICE } from '@my-backoffice/app/shared/auth/authentication.token';
import { countryFlagsFactory } from '@my-backoffice/app/shared/factories/country-flags.factory';
import { dateFactory } from '@my-backoffice/app/shared/factories/date-factory/date.factory';
import { localInitProvider, httpLoaderFactory } from '@my-backoffice/app/shared/factories/i18n.factory';
import { translationFactory } from '@my-backoffice/app/shared/factories/translation.factory';
import { AuthInterceptor } from '@my-backoffice/app/shared/interceptors/auth.interceptor';
import { RequestTraceInterceptor } from '@my-backoffice/app/shared/interceptors/request-trace.interceptor';
import { APPCUES_ACCOUNT_ID_PROVIDER } from '@my-backoffice/app/shared/providers/appcues-account-id.provider';
import { BROWSER_WINDOW_PROVIDER } from '@my-backoffice/app/shared/providers/browser-window.provider';
import { GOOGLE_ANALYTICS_AUTH_PROVIDER } from '@my-backoffice/app/shared/providers/google-analytics-auth.provider';
import { GOOGLE_ANALYTICS_ID_PROVIDER } from '@my-backoffice/app/shared/providers/google-analytics-id.provider';
import { GOOGLE_ANALYTICS_INIT_PROVIDER } from '@my-backoffice/app/shared/providers/google-analytics-init.provider';
import { GOOGLE_ANALYTICS_PREVIEW_PROVIDER } from '@my-backoffice/app/shared/providers/google-analytics-preview.provider';
import { LOGROCKET_ENVIRONMENT_ID_INIT_PROVIDER } from '@my-backoffice/app/shared/providers/logrocket-environment-id-init.provider';
import { LOGROCKET_ENVIRONMENT_ID_PROVIDER } from '@my-backoffice/app/shared/providers/logrocket-environment-id.provider';
import { SERVICE_WORKER_PROVIDER } from '@my-backoffice/app/shared/providers/service-worker.provider';
import { SHOULD_SESSION_STORAGE_ENCRYPT_PROVIDER } from '@my-backoffice/app/shared/providers/should-session-storage-encrypt.provider';
import { WINDOW_PROVIDER } from '@my-backoffice/app/shared/providers/window.provider';
import { AppBrowserDetectService } from '@my-backoffice/app/shared/services/browser-services/app-browser-detect/app-browser-detect.service';
import { CommonRouteReuseStrategy } from '@my-backoffice/app/shared/services/common-route-reuse-strategy/common-route-reuse-strategy.service';
import { HttpMockInterceptor } from '@my-backoffice/app/shared/services/http-mock-handler/http-mock-interceptor.service';
import { HttpErrorHandlerService } from '@my-backoffice/app/shared/services/http-services/http-error-handler/http-error-handler.service';
import { WINDOW } from '@my-backoffice/app/shared/tokens/window.token';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpBackend } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import { enableProdMode, provideZoneChangeDetection, isDevMode, APP_INITIALIZER, FactoryProvider, ValueProvider, LOCALE_ID, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateService, TranslateModule, TranslateLoader } from '@ngx-translate/core';
import {
  QimaSnackbarService,
  QIMA_COUNTRY_FLAGS_TOKEN,
  QIMA_DATE_FACTORY_TOKEN,
  QIMA_ASSETS_PATH_TOKEN,
  QIMA_TRANSLATION_FACTORY_TOKEN,
  QimaModule,
  QimaDateModule,
  QimaInputDateModule,
  QimaTranslationModule,
  QimaLoggerModule,
  EQimaLoggerLevel,
} from '@qima/ngx-qima';
import { QSP_FORM_ITEM_CONFIG_TOKEN, IQspFormItemConfig, EQspSize, EQspFormItemVerticalAlign, EQspOrientation } from '@qima/qsp-ui-library';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CookieModule } from 'ngx-cookie';

registerLocaleData(localeEs, 'es-ES');

declare let ENV_TYPE: 'dev' | 'pp' | 'prod';

if (ENVIRONMENT[ENV_TYPE].production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserAnimationsModule,
      QimaModule.forRoot(),
      AppRoutingModule,
      QimaDateModule,
      QimaInputDateModule.forRoot({
        placeholder: 'Select date',
      }),
      HttpClientModule,
      QimaTranslationModule.forRoot(),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpBackend],
        },
      }),
      // Used by ngx-qima library
      AngularSvgIconModule.forRoot(),
      CookieModule.forRoot(),
      QimaLoggerModule.forRoot({
        loggerLevel: EQimaLoggerLevel.ERROR,
      }),
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      })
    ),
    {
      provide: QSP_FORM_ITEM_CONFIG_TOKEN,
      useFactory(): IQspFormItemConfig {
        return {
          inputSize: EQspSize.NONE,
          inputWidth: 386,
          labelWidth: 158,
          labelSize: EQspSize.NONE,
          verticalAlign: EQspFormItemVerticalAlign.BASELINE,
          orientation: EQspOrientation.HORIZONTAL,
        };
      },
    },
    QimaSnackbarService,
    HttpErrorHandlerService,
    Office365HttpAuthInterceptor,
    ...(isDevMode()
      ? [
          {
            provide: HTTP_INTERCEPTORS,
            useClass: LifeSciencesStudyMockInterceptor,
            multi: true,
          },
          {
            provide: HTTP_INTERCEPTORS,
            useClass: Office365PermissionsMockInterceptor,
            multi: true,
          },
        ]
      : []),
    WINDOW_PROVIDER,
    BROWSER_WINDOW_PROVIDER,
    SHOULD_SESSION_STORAGE_ENCRYPT_PROVIDER,
    SERVICE_WORKER_PROVIDER,
    GOOGLE_ANALYTICS_ID_PROVIDER,
    GOOGLE_ANALYTICS_AUTH_PROVIDER,
    GOOGLE_ANALYTICS_PREVIEW_PROVIDER,
    GOOGLE_ANALYTICS_INIT_PROVIDER,
    APPCUES_ACCOUNT_ID_PROVIDER,
    LOGROCKET_ENVIRONMENT_ID_PROVIDER,
    LOGROCKET_ENVIRONMENT_ID_INIT_PROVIDER,
    {
      provide: APP_INITIALIZER,
      useFactory: localInitProvider,
      deps: [TranslateService, AppBrowserDetectService],
      multi: true,
    },
    {
      provide: QIMA_COUNTRY_FLAGS_TOKEN,
      useFactory: countryFlagsFactory,
      deps: [WINDOW],
    } as FactoryProvider,
    {
      provide: QIMA_DATE_FACTORY_TOKEN,
      useFactory: dateFactory,
    } as FactoryProvider,
    {
      provide: QIMA_ASSETS_PATH_TOKEN,
      useValue: 'ngx-qima/assets/',
    } as ValueProvider,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestTraceInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpMockInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: Office365ErrorHandlerInterceptor,
      multi: true,
    },
    {
      provide: QIMA_TRANSLATION_FACTORY_TOKEN,
      useFactory: translationFactory,
      deps: [TranslateService, LOCALE_ID],
    } as FactoryProvider,
    {
      provide: RouteReuseStrategy,
      useClass: CommonRouteReuseStrategy,
    },
    {
      provide: AUTHENTICATION_SERVICE,
      useExisting: Office365AuthenticationService,
    },
  ],
}).catch((err): void => {
  console.error(err);
});
